import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Waveform, Detective, TrendUp, ShieldCheck } from "@phosphor-icons/react";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", category: params.get("category") || "" });
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);
  useEffect(() => { api.get("/branding-categories").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.category);
      toast.success("Welcome to SMI");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden grain">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1675726205553-4e348f24da2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxpbmZsdWVuY2VyJTIwcG9ydHJhaXQlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzg2OTMyNTQ3fDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/70 to-[#080808]/20" />
        <div className="absolute inset-0 spotlight" />
        <div className="relative flex items-center gap-2">
          <Waveform size={30} weight="fill" className="text-primary" />
          <span className="font-head font-bold text-2xl tracking-tighter">SMI</span>
        </div>
        <div className="relative">
          <div className="overline text-primary mb-3">SOCIAL MEDIA INFLUENCER PLATFORM</div>
          <h2 className="font-head font-bold text-5xl leading-[0.95] tracking-tighter mb-6">
            Own your <span className="gold-foil">narrative</span><br />across the entire web.
          </h2>
          <div className="space-y-4 mt-8">
            {[
              [Detective, "Deep web & SEO reputation audits"],
              [TrendUp, "AI content that floods search with positive signal"],
              [ShieldCheck, "Built for influencers, artists, founders & creators"],
            ].map(([Icon, text], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                <Icon size={20} weight="bold" className="text-primary" />
                {text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative overline text-muted-foreground">POWERED BY GPT-5.6 · NANO BANANA VISUALS</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Waveform size={28} weight="fill" className="text-primary" />
            <span className="font-head font-extrabold text-2xl tracking-tighter">SMI</span>
          </div>
          <h1 className="font-head font-bold text-3xl tracking-tight mb-1">
            {mode === "login" ? "Command center login" : "Create your account"}
          </h1>
          <p className="text-muted-foreground text-sm mb-8">
            {mode === "login" ? "Access your reputation dashboard." : "Start controlling your online image."}
          </p>

          {mode === "register" && (
            <Field
              label="Full name"
              testid="name-input"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Jordan Vega"
            />
          )}
          {mode === "register" && (
            <div className="mb-4">
              <label className="overline text-muted-foreground block mb-2">Your category</label>
              <select value={form.category} data-testid="category-select"
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200">
                <option value="">Select your lane…</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <Field
            label="Email"
            type="email"
            testid="email-input"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            placeholder="you@brand.com"
          />
          <Field
            label="Password"
            type="password"
            testid="password-input"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            placeholder="••••••••"
          />

          {error && (
            <div className="text-destructive text-sm mb-4 font-mono" data-testid="auth-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="shimmer-cta w-full bg-primary text-primary-foreground font-bold py-3 text-sm uppercase tracking-wider transition-[transform,box-shadow] duration-300 hover:shadow-[0_0_24px_rgba(233,172,12,0.45)] hover:-translate-y-0.5 disabled:opacity-50"
          >
            {busy ? "Working…" : mode === "login" ? "Enter SMI" : "Create account"}
          </button>

          <button
            type="button"
            data-testid="toggle-mode-btn"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="w-full text-center text-sm text-muted-foreground hover:text-white mt-4 transition-colors duration-200"
          >
            {mode === "login" ? "No account? Create one" : "Already have an account? Log in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="mb-4">
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200"
      />
    </div>
  );
}
